import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import {
  ILegalClientDocumentTypeEntry,
  ILegalClientDocumentVersion,
  formatFileSize,
} from '../../Interfaces/ILegalClientDocumentVersion';
import { DocumentInlinePreview } from './DocumentInlinePreview';
import { ImageZoomDialog } from './ImageZoomDialog';

interface IProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  typeEntry: ILegalClientDocumentTypeEntry | null;
}

const formatDateTime = (value: string | null | undefined): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime())
    ? '—'
    : parsed.toLocaleString('de-DE', {
        dateStyle: 'short',
        timeStyle: 'short',
      });
};

const formatDate = (value: string | null | undefined): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime())
    ? '—'
    : parsed.toLocaleDateString('de-DE');
};

const versionLabel = (version: ILegalClientDocumentVersion): string =>
  `Version ${version.VersionNumber}${version.isCurrent === true ? ' (aktuell)' : ''} — ${formatDateTime(version.Created_at)}`;

const userLabel = (version: ILegalClientDocumentVersion): string => {
  const name = [version.UserFirstName, version.UserLastName]
    .filter(part => part !== null && part !== undefined && part !== '')
    .join(' ');

  return name === '' ? 'System / Mandantenportal' : name;
};

/**
 * Zwei Versionen eines Dokumenttyps gegenüberstellen — mit allen Dateien der
 * jeweiligen Version, damit z. B. bei einem Ausweis Vorder- und Rückseite
 * gemeinsam betrachtet werden.
 */
export const DocumentVersionCompareDialog: React.FC<IProps> = props => {
  const versionArray = props.typeEntry?.VersionArray ?? [];

  const [leftKey, setLeftKey] = useState('');
  const [rightKey, setRightKey] = useState('');
  /** Welche Datei der jeweiligen Version gerade in der Vorschau steht. */
  const [leftFileIndex, setLeftFileIndex] = useState(0);
  const [rightFileIndex, setRightFileIndex] = useState(0);
  const [zoomUrl, setZoomUrl] = useState<string | null>(null);

  /** Bestandsversionen können `null` als ID haben — daher über den Index adressieren. */
  const keyOf = (index: number) => String(index);

  useEffect(() => {
    if (props.isOpen) {
      setLeftKey((props.typeEntry?.VersionArray ?? []).length > 1 ? '1' : '0');
      setRightKey('0');
      setLeftFileIndex(0);
      setRightFileIndex(0);
    }
  }, [props.isOpen, props.typeEntry]);

  const left = versionArray[Number(leftKey)];
  const right = versionArray[Number(rightKey)];

  const renderVersionColumn = (
    version: ILegalClientDocumentVersion | undefined,
    fileIndex: number,
    setFileIndex: (index: number) => void
  ) => {
    if (version === undefined) {
      return (
        <Typography variant='body2' color='text.secondary'>
          Keine Version gewählt.
        </Typography>
      );
    }

    return (
      <Stack spacing={1} sx={{ height: '100%', width: '100%' }}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography variant='subtitle2'>
            Version {version.VersionNumber}
          </Typography>
          {version.isCurrent === true && (
            <Chip size='small' color='primary' label='aktuell' />
          )}
        </Stack>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            Hochgeladen: {formatDateTime(version.Created_at)}
            <br />
            Benutzer: {userLabel(version)}
            <br />
            Gültig bis: {formatDate(version.ValidUntil)}
            {version.Note !== null &&
              version.Note !== undefined &&
              version.Note !== '' && (
                <>
                  <br />
                  Anmerkung: {version.Note}
                </>
              )}
          </Typography>

          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Datei</TableCell>
                <TableCell width={110}>Größe</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {version.FileArray.map((file, index) => (
                <TableRow
                  key={`compare-${version.VersionNumber}-${file.idDocument}`}
                  hover
                  selected={index === fileIndex}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setFileIndex(index)}
                >
                  <TableCell>
                    {file.FileName}
                    {file.Label !== null &&
                      file.Label !== undefined &&
                      file.Label !== '' && (
                        <Typography variant='caption' color='text.secondary'>
                          <br />
                          {file.Label}
                        </Typography>
                      )}
                  </TableCell>
                  <TableCell>{formatFileSize(file.FileSize)}</TableCell>
                </TableRow>
              ))}
              {version.FileArray.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2}>
                    <Typography variant='body2' color='text.secondary'>
                      Keine Dateien.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        {version.FileArray.length > 0 && (
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <DocumentInlinePreview
              idDocument={version.FileArray[fileIndex]?.idDocument}
              fileName={version.FileArray[fileIndex]?.FileName ?? ''}
              height={460}
              onZoom={setZoomUrl}
              pendingData={version.FileArray[fileIndex]?.Data}
              pendingIdMIMEType={version.FileArray[fileIndex]?.idMIMEType}
            />
          </Box>
        )}
      </Stack>
    );
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={() => props.setIsOpen(false)}
      maxWidth='lg'
      fullWidth
    >
      <DialogTitle>
        Versionen vergleichen
        <Typography variant='body2' color='text.secondary'>
          {props.typeEntry?.LegalClientDocumentType}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {versionArray.length < 2 ? (
          <Alert severity='info'>
            Für einen Vergleich sind mindestens zwei Versionen nötig. Hier liegt
            bislang {versionArray.length === 0 ? 'keine' : 'nur eine'} Version
            vor.
          </Alert>
        ) : (
          <>
            <Alert severity='info' sx={{ mb: 2 }}>
              Verglichen wird die gesamte Version mit allen ihren Dateien. Unter
              jeder Spalte steht die Datei, die in der Liste darüber ausgewählt
              ist — bei mehrteiligen Dokumenten lässt sich so etwa Vorderseite
              gegen Vorderseite stellen. Eine automatische Auswertung der
              Unterschiede (Name, Anschrift, Ausweisnummer, Ablaufdatum) ist
              noch nicht enthalten.
            </Alert>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              sx={{ mb: 2 }}
            >
              <TextField
                label='Ältere Version'
                value={leftKey}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setLeftKey(event.target.value);
                  setLeftFileIndex(0);
                }}
                size='small'
                select
                fullWidth
              >
                {versionArray.map((version, index) => (
                  <MenuItem key={`left-${index}`} value={keyOf(index)}>
                    {versionLabel(version)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label='Neuere Version'
                value={rightKey}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setRightKey(event.target.value);
                  setRightFileIndex(0);
                }}
                size='small'
                select
                fullWidth
              >
                {versionArray.map((version, index) => (
                  <MenuItem key={`right-${index}`} value={keyOf(index)}>
                    {versionLabel(version)}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems='stretch'
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  p: 2,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  display: 'flex',
                }}
              >
                {renderVersionColumn(left, leftFileIndex, setLeftFileIndex)}
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  p: 2,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  display: 'flex',
                }}
              >
                {renderVersionColumn(right, rightFileIndex, setRightFileIndex)}
              </Box>
            </Stack>
          </>
        )}
      </DialogContent>
      <ImageZoomDialog objectUrl={zoomUrl} onClose={() => setZoomUrl(null)} />

      <DialogActions>
        <Button variant='contained' onClick={() => props.setIsOpen(false)}>
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  );
};
